var _a;
import { EventSurface } from './events';
// Used for the `screen_transition` client events: https://pinwheel.atlassian.net/browse/CON-502
// Since the `ScreenName` we expose to clients are not always 1:1 with the `EventSurface` names
// we use for internal segment events (e.g, `ScreenName.PROVIDER_CONFIRMATION` actually includes
// multiple event surfaces), we want to create a new mapping here to explicitly track the values
// and mappings we expose. Documentation: https://docs.pinwheelapi.com/docs/link-1#screentransition
export var ScreenName;
(function (ScreenName) {
    ScreenName["SEARCH_DEFAULT"] = "SEARCH_DEFAULT";
    ScreenName["SEARCH_RESULTS"] = "SEARCH_RESULTS";
    ScreenName["SEARCH_PLATFORM_ONLY"] = "SEARCH_PLATFORM_ONLY";
    ScreenName["LOGIN"] = "LOGIN";
    ScreenName["PROVIDER_CONFIRMATION"] = "PROVIDER_CONFIRMATION";
    ScreenName["DD_AMOUNT_SELECTION"] = "DD_AMOUNT_SELECTION";
    ScreenName["DD_CONFIRMATION"] = "DD_CONFIRMATION";
    ScreenName["JOB_SUCCESS"] = "JOB_SUCCESS";
    ScreenName["JOB_ERROR"] = "JOB_ERROR";
    ScreenName["EXIT_CONFIRMATION"] = "EXIT_CONFIRMATION";
    ScreenName["GUIDED_SWITCH_PRIMER"] = "GUIDED_SWITCH_PRIMER";
    ScreenName["APP_CLIP_PRIMER"] = "APP_CLIP_PRIMER";
    ScreenName["CREDENTIALLESS_ADP"] = "CREDENTIALLESS_ADP";
    ScreenName["WIZARD"] = "PRE_FLOW_SURVEY";
    ScreenName["BILL_NAVIGATOR"] = "BILL_NAVIGATOR";
    ScreenName["TERMS_OF_SERVICE"] = "TERMS_OF_SERVICE";
    ScreenName["PRIVACY_POLICY"] = "PRIVACY_POLICY";
    ScreenName["PII_NAME_ENTRY"] = "PII_NAME_ENTRY";
    ScreenName["PII_TEL_ENTRY"] = "PII_TEL_ENTRY";
    ScreenName["PII_DOB_ENTRY"] = "PII_DOB_ENTRY";
    ScreenName["PII_SSN4_ENTRY"] = "PII_SSN4_ENTRY";
    ScreenName["PII_SSN9_ENTRY"] = "PII_SSN9_ENTRY";
    ScreenName["PII_REVIEW"] = "PII_REVIEW";
    ScreenName["PLATFORM_ELIGIBILITY_LOOKUP_LOADING"] = "PLATFORM_ELIGIBILITY_LOOKUP_LOADING";
    ScreenName["PLATFORM_ELIGIBILITY_LOOKUP_RESULTS"] = "PLATFORM_ELIGIBILITY_LOOKUP_RESULTS";
    ScreenName["PLATFORM_ELIGIBILITY_LOOKUP_HELP"] = "PLATFORM_ELIGIBILITY_LOOKUP_HELP";
})(ScreenName || (ScreenName = {}));
// NOTE: When adding new event surfaces, check with product to see if it should
// also map to a new or existing `ScreenName`, so that a `screen_transition`
// client event would be fired on that surface. If not, map it to `null`. Note
// that we want to use an exhaustive mapping here so that we don't accidentally
// forget to update screen mappings when new surfaces are added. Additionally,
// add a new entry for it within the screen transitions table in the public Link
// docs: https://docs.pinwheelapi.com/docs/link-1#screentransition
export var SURFACE_TO_SCREEN_MAP = (_a = {},
    _a[EventSurface.INTRO] = null,
    _a[EventSurface.ABOUT_INFO] = null,
    _a[EventSurface.ADP_IFRAME] = ScreenName.CREDENTIALLESS_ADP,
    _a[EventSurface.BANK_INFO_INPUT] = null,
    _a[EventSurface.BANK_INFO_CONFIRMATION] = null,
    _a[EventSurface.TERMS_OF_SERVICE] = ScreenName.TERMS_OF_SERVICE,
    _a[EventSurface.PRIVACY_POLICY] = ScreenName.PRIVACY_POLICY,
    _a[EventSurface.EXIT_CONFIRMATION] = ScreenName.EXIT_CONFIRMATION,
    _a[EventSurface.EXIT_CONFIRMATION_ALT] = ScreenName.EXIT_CONFIRMATION,
    _a[EventSurface.SEARCH_DEFAULT] = ScreenName.SEARCH_DEFAULT,
    _a[EventSurface.SEARCH_RESULTS] = ScreenName.SEARCH_RESULTS,
    _a[EventSurface.SEARCH_ERROR] = null,
    _a[EventSurface.SEARCH_PLATFORM_ONLY] = ScreenName.SEARCH_PLATFORM_ONLY,
    _a[EventSurface.SEARCH_WORKDAY_ONLY] = ScreenName.PROVIDER_CONFIRMATION,
    _a[EventSurface.PROVIDER_CONFIRMATION_BOUNDED] = ScreenName.PROVIDER_CONFIRMATION,
    _a[EventSurface.PROVIDER_CONFIRMATION_UNBOUNDED] = ScreenName.PROVIDER_CONFIRMATION,
    _a[EventSurface.LOGIN] = ScreenName.LOGIN,
    _a[EventSurface.LOGIN_ERROR] = null,
    _a[EventSurface.LOGIN_CONFIRMATION] = null,
    _a[EventSurface.ACCOUNT_RECOVERY] = null,
    _a[EventSurface.ACCOUNT_RECOVERY_ERROR] = null,
    _a[EventSurface.ACCOUNT_RECOVERY_CONFIRMATION] = null,
    _a[EventSurface.DD_PAYMENT] = null,
    _a[EventSurface.JOB_ERROR] = ScreenName.JOB_ERROR,
    _a[EventSurface.MODAL_INITIALIZATION] = null,
    _a[EventSurface.LOADING] = null,
    _a[EventSurface.REAUTH] = null,
    _a[EventSurface.REAUTH_ERROR] = null,
    _a[EventSurface.MANUAL_INSTRUCTIONS_FALLBACK] = null,
    _a[EventSurface.MANUAL_INSTRUCTIONS] = null,
    _a[EventSurface.MULTIPLE_ACCOUNTS_SELECTION] = null,
    _a[EventSurface.ALLOCATION_VIEWING] = null,
    _a[EventSurface.ALLOCATION_VIEWING_LOADING] = null,
    _a[EventSurface.ALLOCATION_VIEWING_ERROR] = null,
    _a[EventSurface.INVALID_CREDENTIALS_ERROR] = null,
    _a[EventSurface.DD_FORM_PRIMER] = null,
    _a[EventSurface.EMPLOYER_INPUT] = null,
    _a[EventSurface.NAME_INPUT] = null,
    _a[EventSurface.DD_AMOUNT_SELECTION] = ScreenName.DD_AMOUNT_SELECTION,
    _a[EventSurface.DD_CONFIRMATION] = ScreenName.DD_CONFIRMATION,
    _a[EventSurface.DD_FORM_CONFIRM] = null,
    _a[EventSurface.JOB_SUCCESS] = ScreenName.JOB_SUCCESS,
    _a[EventSurface.SIGNATURE_SELECTION] = null,
    _a[EventSurface.DD_FORM_SUCCESS] = null,
    _a[EventSurface.CARD_SWITCH_PROMPT] = null,
    _a[EventSurface.PROVIDER_CONFIRMATION_PRIMER] = null,
    _a[EventSurface.DOCUMENT_UPLOADS_PRIMER] = null,
    _a[EventSurface.DOCUMENT_UPLOADS_LIST] = null,
    _a[EventSurface.DOCUMENT_UPLOADS_SUBMITTED] = null,
    _a[EventSurface.DOCUMENT_UPLOADS_REPAIR] = null,
    _a[EventSurface.DOCUMENT_UPLOADS_DELETE] = null,
    _a[EventSurface.GUIDED_SWITCH_PRIMER] = ScreenName.GUIDED_SWITCH_PRIMER,
    _a[EventSurface.GUIDED_SWITCH] = null,
    _a[EventSurface.PLATFORM_ELIGIBILITY_LOOKUP_HELP] = ScreenName.PLATFORM_ELIGIBILITY_LOOKUP_HELP,
    _a[EventSurface.PLATFORM_ELIGIBILITY_LOOKUP_LOADING] = ScreenName.PLATFORM_ELIGIBILITY_LOOKUP_LOADING,
    _a[EventSurface.PLATFORM_ELIGIBILITY_LOOKUP_RESULTS] = ScreenName.PLATFORM_ELIGIBILITY_LOOKUP_RESULTS,
    _a[EventSurface.DDS_IFRAME] = null,
    _a[EventSurface.LOGIN_IFRAME] = null,
    _a[EventSurface.RETURN_TO_SEARCH] = null,
    _a[EventSurface.APP_CLIP_PRIMER] = ScreenName.APP_CLIP_PRIMER,
    _a[EventSurface.WIZARD_ERROR] = ScreenName.WIZARD,
    _a[EventSurface.WIZARD_INCOME_SOURCES] = ScreenName.WIZARD,
    _a[EventSurface.WIZARD_INCOME_SOURCE_1099] = ScreenName.WIZARD,
    _a[EventSurface.WIZARD_INCOME_SOURCE_W2] = ScreenName.WIZARD,
    _a[EventSurface.WIZARD_INCOME_SOURCE_BENEFITS] = ScreenName.WIZARD,
    _a[EventSurface.WIZARD_PAYMENT_METHODS] = ScreenName.WIZARD,
    _a[EventSurface.BILL_NAVIGATOR] = ScreenName.BILL_NAVIGATOR,
    _a[EventSurface.PII_NAME_ENTRY] = ScreenName.PII_NAME_ENTRY,
    _a[EventSurface.PII_TEL_ENTRY] = ScreenName.PII_TEL_ENTRY,
    _a[EventSurface.PII_DOB_ENTRY] = ScreenName.PII_DOB_ENTRY,
    _a[EventSurface.PII_SSN4_ENTRY] = ScreenName.PII_SSN4_ENTRY,
    _a[EventSurface.PII_SSN9_ENTRY] = ScreenName.PII_SSN9_ENTRY,
    _a[EventSurface.PII_REVIEW] = ScreenName.PII_REVIEW,
    _a);
